import { create } from "zustand";

// Theme modes:
//   - "light"   force light, ignore OS preference
//   - "dark"    force dark
//   - "system"  follow OS (matchMedia 'prefers-color-scheme: dark')
// Persisted to localStorage under "lumi-theme". An inline <script> in
// index.html applies the right class BEFORE React mounts to avoid FOUC; this
// store is just the React-side mirror that the UI controls.

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "lumi-theme";

function readStored(): ThemeMode {
  if (typeof localStorage === "undefined") return "system";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

export function resolveEffective(mode: ThemeMode): "light" | "dark" {
  if (mode === "light" || mode === "dark") return mode;
  if (typeof matchMedia === "undefined") return "dark";
  return matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToDom(effective: "light" | "dark") {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", effective === "dark");
}

interface ThemeState {
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: readStored(),
  setMode: (m) => {
    set({ mode: m });
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* private mode */ }
    applyToDom(resolveEffective(m));
    // Force a re-render of subscribers that depend on effective theme.
    // get() is enough to keep TS happy in case future extensions need it.
    void get();
  },
}));
